import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FAQ from "@/components/FAQ";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { CheckCircle2, Shield, Zap, Lock, FileText, BadgeCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";

const Pricing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Complete Gamification Strategy Package",
    "description": "AI-powered personalized gamification strategy with implementation guidelines, technical specifications, and design guidelines.",
    "offers": {
      "@type": "Offer",
      "price": "5.00",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "500"
    }
  };

  const handleGetStarted = async () => {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user?.email) {
      // User is logged in, proceed directly to checkout
      await processCheckout(user.email, user.user_metadata?.full_name || "");
    } else {
      // Show dialog to collect email
      setShowCheckoutDialog(true);
    }
  };

  const processCheckout = async (email: string, name: string) => {
    try {
      setIsLoading(true);
      console.log("[PRICING] Starting checkout for:", email);

      // Create checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { email, name }
      });

      console.log("[PRICING] Checkout response:", { data, error });

      if (error) throw error;

      if (data?.url) {
        // Close dialog and redirect to Stripe Checkout
        // Using window.location.href instead of window.open for mobile compatibility
        setShowCheckoutDialog(false);
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[PRICING] Payment error:', errorMessage, error);
      toast.error(`Failed to start checkout: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    "Customized gamification strategy",
    "Engagement tier recommendation (Core/Moderate/High)",
    "Specific implementation guidelines",
    "Technical specifications (JSON file)",
    "Design guidelines (PDF)",
    "Implementation roadmap",
  ];

  return (
    <>
      <SEO 
        title="Pricing"
        description="Get your complete gamification strategy package for $5. One-time payment, 30-day money-back guarantee. Strategy, specs, and roadmap included."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/pricing"
        keywords="gamification pricing, app strategy cost, gamification package, affordable app consulting"
        ogType="product"
        jsonLd={pricingSchema}
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main>
          {/* Hero Section */}
          <section className="pt-32 pb-12 px-4" aria-labelledby="pricing-heading">
            <header className="max-w-4xl mx-auto text-center space-y-6">
              <h1 id="pricing-heading" className="text-5xl md:text-6xl font-bold">Simple, Transparent Pricing</h1>
              <p className="text-xl text-muted-foreground">
                One-time payment. Lifetime access. No subscriptions.
              </p>
            </header>
          </section>

      {/* Pricing Card */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-10 shadow-2xl border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5">
            <div className="text-center space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-2">
                  Complete Gamification Strategy Package
                </h2>
                <div className="flex items-baseline justify-center gap-2 my-6">
                  <span className="text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    $5
                  </span>
                  <span className="text-2xl text-muted-foreground">one-time</span>
                </div>
              </div>

              <div className="space-y-4 text-left">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="lg"
                onClick={handleGetStarted}
                disabled={isLoading}
                className="w-full text-xl py-7 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg disabled:opacity-50"
              >
                {isLoading ? "Starting checkout..." : "Get Started - $5"}
              </Button>

              <div className="grid sm:grid-cols-3 gap-4 pt-2 text-left">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">Your idea stays yours</span> — your inputs are never reviewed, sold, or shared
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">Instant PDF delivery</span> — yours to keep permanently
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <BadgeCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">30-day money-back guarantee</span> — no questions asked
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-3 pt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  <span>30-Day Money-Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  <span>Secure payment via Stripe</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            Why This vs. Hiring a Consultant?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 bg-muted/50">
              <h3 className="text-2xl font-bold mb-6">Traditional Consultant</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground mt-2" />
                  <span className="text-muted-foreground">$5,000 - $15,000 investment</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground mt-2" />
                  <span className="text-muted-foreground">Weeks to months timeline</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground mt-2" />
                  <span className="text-muted-foreground">Multiple meetings required</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground mt-2" />
                  <span className="text-muted-foreground">Generic frameworks</span>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5">
              <h3 className="text-2xl font-bold mb-6 text-primary">This Tool</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Only $5 one-time payment</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>10 minutes to complete</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Instant delivery (2 minutes)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>AI-powered personalization</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-3xl mx-auto">
          <FAQ />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold">
            Ready to Transform Your App?
          </h2>
          <p className="text-xl text-muted-foreground">
            Get your personalized gamification strategy in minutes, not weeks
          </p>
          <Button 
            size="lg"
            onClick={handleGetStarted}
            disabled={isLoading}
            className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg disabled:opacity-50"
          >
            {isLoading ? "Starting checkout..." : "Get Started - $5"}
            </Button>
          </div>
        </section>
        </main>

        <Footer />
      </div>

      <CheckoutDialog
        open={showCheckoutDialog}
        onOpenChange={setShowCheckoutDialog}
        onSubmit={processCheckout}
        isLoading={isLoading}
      />
    </>
  );
};

export default Pricing;
